import { ReactElement, useCallback, useState } from "react";

import { Button, HStack, Text, useToast } from "@chakra-ui/react";

import { DownloadIcon } from "icons";
import useDownloadFile from "hooks/useDownloadFile";
import { DEFAULT_EMPTY_VALUE } from "util/statHelpers";

const FileDownloadLink = ({
  docEntry,
  onFetchFiles,
  label,
  onSetFiles,
}: {
  docEntry: number | undefined;
  label: string | typeof DEFAULT_EMPTY_VALUE | number;
  onSetFiles?: (files: DocumentsFile[]) => void;
  onFetchFiles: (
    id: number | undefined
  ) => Promise<DocumentsFile[] | undefined>;
}): ReactElement => {
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const downloadDocumentFile = useDownloadFile();

  const onClick = useCallback(() => {
    setIsLoading(true);
    onFetchFiles(docEntry)
      .then((files) => {
        if (!files) {
          throw new Error("Invalid or missing file download URL.");
        }
        if (files.length === 1) {
          downloadDocumentFile(files[0])?.then(() => {
            setIsLoading(false);
          });
        } else {
          onSetFiles?.(files);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        toast({
          position: "top-right",
          description: error.message,
          duration: 2000,
          status: "error",
        });
        setIsLoading(false);
      });
  }, [docEntry, downloadDocumentFile, onFetchFiles, onSetFiles, toast]);

  return (
    <Button
      onClick={onClick}
      variant="link"
      isLoading={isLoading}
      isDisabled={isLoading}
      loadingText="Working"
      size="sm"
    >
      <HStack spacing={1} align="flex-start">
        <Text color="inherit" fontWeight="semibold" textStyle="body2">
          {label}
        </Text>
        <DownloadIcon />
      </HStack>
    </Button>
  );
};

export default FileDownloadLink;
